/**
 * Profile Share — public API client.
 *
 * Deliberately NOT the shared axios instance. That client attaches session
 * credentials and the mock-user header, and this page is public: the visitor
 * has no account, and a signed-in viewer's cookie has no business riding along
 * on a request that ignores authentication entirely. Plain fetch with
 * `credentials: 'omit'` keeps that true by construction.
 *
 * The token is the only credential. It comes from the URL and is passed
 * straight back in the path — never logged, never put in a query string (query
 * strings are far likelier to end up in analytics and proxy logs).
 *
 * See docs/design/profile-share.md §5.
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

async function post(token, path, body) {
  const res = await fetch(
    `${API_BASE}/api/profileshare/${encodeURIComponent(token)}${path}`,
    {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    }
  );

  if (!res.ok) {
    // 404 covers unknown AND revoked tokens — the server makes them
    // indistinguishable on purpose, so don't invent a distinction here.
    const err = new Error(res.status === 404 ? 'not_found' : `http_${res.status}`);
    err.status = res.status;
    throw err;
  }

  return res.status === 204 ? null : res.json();
}

/** Start a visit. Returns profile, role views, and the current chat mode. */
export function startSession(token) {
  return post(token, '/session', {});
}

/**
 * Ask a question.
 *
 * `anchor` records which line prompted it, so an escalation arrives with the
 * requirement attached rather than as a bare question.
 */
export function askQuestion(token, sessionId, message, anchor) {
  return post(token, '/chat', {
    session_id: sessionId,
    message,
    ...(anchor ? { anchor } : {}),
  });
}

/**
 * Report an interaction event. Fire-and-forget: instrumentation must never
 * break the page or block the reader, so failures are swallowed.
 */
export function recordEvent(token, sessionId, kind, payload) {
  return post(token, '/event', { session_id: sessionId, kind, payload: payload || {} })
    .catch(() => {});
}
